/**
 * The agent's formatting tool: fonts, colours, spacing and alignment on the
 * whole document, on a span of paragraphs, or on a named style. Formatting
 * is written with change tracking on, so it shows up as "Formatted"
 * revisions the user can reject like any other. Page setup (margins,
 * orientation) has no Office.js API in the current Word requirement sets
 * and is reported back as unsupported rather than faked.
 */
import { documentCapabilities } from "./snapshot";
import { locateParagraph } from "./wordTools";

export type FontSpec = {
  name?: string;
  size?: number;
  color?: string;
  highlight?: string | null;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
};

export type ParagraphSpec = {
  alignment?: "left" | "centered" | "right" | "justified";
  line_spacing?: number;
  space_before?: number;
  space_after?: number;
  /** A built-in style name to apply, e.g. "Heading 2" or "List Paragraph". */
  style?: string;
};

export type FormatOp =
  | { target: "document"; font?: FontSpec; paragraph?: ParagraphSpec }
  | { target: "style"; style_name: string; font?: FontSpec; paragraph?: ParagraphSpec }
  | {
      target: "paragraphs";
      start: number;
      end: number;
      expected_text: string;
      font?: FontSpec;
      paragraph?: ParagraphSpec;
    };

export type FormatOutcome = {
  target: FormatOp["target"];
  status: "applied" | "stale" | "missing" | "unsupported" | "error";
  message?: string;
  current_text?: string;
};

export type FormatResult = { outcomes: FormatOutcome[]; tracked_changes: boolean };

const ALIGNMENT: Record<NonNullable<ParagraphSpec["alignment"]>, Word.Alignment> = {
  left: Word.Alignment.left,
  centered: Word.Alignment.centered,
  right: Word.Alignment.right,
  justified: Word.Alignment.justified,
};

const applyFont = (font: Word.Font, spec: FontSpec | undefined): void => {
  if (!spec) return;
  if (spec.name) font.name = spec.name;
  if (typeof spec.size === "number" && spec.size > 0) font.size = spec.size;
  if (spec.color) font.color = spec.color;
  if (spec.highlight !== undefined) font.highlightColor = spec.highlight as string;
  if (typeof spec.bold === "boolean") font.bold = spec.bold;
  if (typeof spec.italic === "boolean") font.italic = spec.italic;
  if (typeof spec.underline === "boolean")
    font.underline = spec.underline ? Word.UnderlineType.single : Word.UnderlineType.none;
};

const applyParagraph = (paragraph: Word.Paragraph, spec: ParagraphSpec | undefined): void => {
  if (!spec) return;
  if (spec.alignment) paragraph.alignment = ALIGNMENT[spec.alignment];
  if (typeof spec.line_spacing === "number" && spec.line_spacing > 0) paragraph.lineSpacing = spec.line_spacing;
  if (typeof spec.space_before === "number") paragraph.spaceBefore = spec.space_before;
  if (typeof spec.space_after === "number") paragraph.spaceAfter = spec.space_after;
  if (spec.style) paragraph.style = spec.style;
};

/**
 * Apply the agent's formatting ops, tracked where the host allows.
 * @param ops - The ops, applied in order.
 * @returns One outcome per op.
 */
export const formatDocument = async (ops: readonly FormatOp[]): Promise<FormatResult> => {
  const caps = documentCapabilities();
  return Word.run(async (context) => {
    const document = context.document;
    let previousMode: Word.ChangeTrackingMode | `${Word.ChangeTrackingMode}` | null = null;
    if (caps.tracked_changes) {
      document.load("changeTrackingMode");
      await context.sync();
      previousMode = document.changeTrackingMode;
      if (previousMode !== Word.ChangeTrackingMode.trackAll)
        document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    }
    const restore = async () => {
      if (caps.tracked_changes && previousMode !== null && previousMode !== Word.ChangeTrackingMode.trackAll) {
        document.changeTrackingMode = previousMode;
        await context.sync();
      }
    };
    const outcomes: FormatOutcome[] = [];
    try {
      const paragraphs = document.body.paragraphs;
      paragraphs.load("items/text");
      await context.sync();
      const texts = paragraphs.items.map((p) => p.text ?? "");

      for (const op of ops) {
        try {
          if (op.target === "document") {
            applyFont(document.body.font, op.font);
            if (op.paragraph) for (const p of paragraphs.items) applyParagraph(p, op.paragraph);
            outcomes.push({ target: op.target, status: "applied" });
          } else if (op.target === "style") {
            if (!caps.styles) {
              outcomes.push({
                target: op.target,
                status: "unsupported",
                message: "This Word cannot edit styles; format the paragraphs instead.",
              });
              continue;
            }
            const style = document.getStyles().getByNameOrNullObject(op.style_name);
            style.load("isNullObject");
            await context.sync();
            if (style.isNullObject) {
              outcomes.push({ target: op.target, status: "missing", message: `No style named "${op.style_name}".` });
              continue;
            }
            applyFont(style.font, op.font);
            if (op.paragraph) {
              const pf = style.paragraphFormat;
              if (op.paragraph.alignment) pf.alignment = ALIGNMENT[op.paragraph.alignment];
              if (typeof op.paragraph.line_spacing === "number" && op.paragraph.line_spacing > 0)
                pf.lineSpacing = op.paragraph.line_spacing;
              if (typeof op.paragraph.space_before === "number") pf.spaceBefore = op.paragraph.space_before;
              if (typeof op.paragraph.space_after === "number") pf.spaceAfter = op.paragraph.space_after;
            }
            outcomes.push({ target: op.target, status: "applied" });
          } else {
            const first = locateParagraph(texts, op.start, op.expected_text);
            if (first === null) {
              const inRange = op.start >= 0 && op.start < texts.length;
              outcomes.push({
                target: op.target,
                status: inRange ? "stale" : "missing",
                ...(inRange && { current_text: texts[op.start] }),
                message: inRange
                  ? "The first paragraph no longer has the expected text; read again."
                  : "No paragraph at this index.",
              });
              continue;
            }
            const last = Math.min(texts.length - 1, first + Math.max(0, op.end - op.start));
            for (let i = first; i <= last; i += 1) {
              const p = paragraphs.items[i];
              applyFont(p.font, op.font);
              applyParagraph(p, op.paragraph);
            }
            outcomes.push({ target: op.target, status: "applied" });
          }
          await context.sync();
        } catch (error) {
          outcomes.push({
            target: op.target,
            status: "error",
            message: error instanceof Error ? error.message : String(error),
          });
        }
      }
    } finally {
      // Whatever failed, the user's own tracking setting comes back.
      await restore();
    }
    return { outcomes, tracked_changes: caps.tracked_changes };
  });
};
